import { normalizeBookieKey } from "../../common/bookmaker-filter-key";
import { ArbCalculatorValues, ArbLeg, ArbitrageRow } from "../types/arbitrage.types";
import { parseKalshiTickerThreshold } from "../../common/direct-betting/tickerMatching";
import { bookSupportsDeeplink } from "../../common/book-deeplink/supports";
import { getSourceTypeForBookmaker } from "../../../../utils/source-type";

/**
 * Bookies whose `netOdds` differs from `odds` because of exchange/PM
 * fees. Centralized here so the arb calculator and display layers
 * agree on which legs need the listed/net distinction.
 */
const PREDICTION_MARKET_BOOKIES = new Set(["kalshi", "polymarket"]);

export { normalizeBookieKey } from "../../common/bookmaker-filter-key";

/**
 * Converts American odds to decimal odds.
 */
export function americanToDecimal(americanOdds: number): number {
  return americanOdds > 0
    ? americanOdds / 100 + 1
    : 100 / Math.abs(americanOdds) + 1;
}

/**
 * Converts American odds to implied probability.
 */
export function americanToImpliedProbability(americanOdds: number): number {
  return americanOdds > 0
    ? 100 / (americanOdds + 100)
    : Math.abs(americanOdds) / (Math.abs(americanOdds) + 100);
}

/**
 * Returns the post-fee American odds for a leg when available, falling
 * back to the listed odds. Lets every consumer treat fee-aware math as
 * the default without thinking about which platform the leg is on.
 *
 * Exported so arb table display layers pick the same "best" leg as
 * {@link calculateArbPercent} / the backend fee-aware arb detector (listed
 * vs net skew was hiding rows and starving Add to slip CTAs).
 */
export function arbLegEffectiveAmerican(leg: {
  odds: number;
  netOdds?: number | null;
  bookie?: string;
}): number {
  if (leg.netOdds != null && leg.netOdds !== 0) return leg.netOdds;
  return leg.odds;
}

function effectiveOdds(leg: { odds: number; netOdds?: number | null; bookie?: string }): number {
  return arbLegEffectiveAmerican(leg);
}

/**
 * Calculates the arb percentage from a set of legs.
 * A negative value means there IS an arb (guaranteed profit).
 * The magnitude is the profit margin percentage.
 *
 * <p>Uses {@code netOdds} when present so PM/exchange fees can't
 * inflate the displayed margin past what the user actually realizes.
 * Sportsbook legs (no {@code netOdds}) reuse {@code odds} unchanged.</p>
 */
export function calculateArbPercent(
  legs: { odds: number; netOdds?: number | null; bookie?: string }[]
): number {
  const totalImplied = legs.reduce(
    (sum, leg) => sum + americanToImpliedProbability(effectiveOdds(leg)),
    0
  );
  return parseFloat(((totalImplied - 1) * 100 * -1).toFixed(2));
}

/**
 * Calculates stakes for each leg given total bet size and odds.
 * Returns the full leg objects with stake and payout.
 *
 * <p>Stake split is computed against {@code netOdds} so PM legs are
 * sized as if you'll receive the post-fee return — which is the
 * outcome the user actually cares about. The displayed payout is
 * still computed against {@code netOdds} for the same reason; the
 * Listed/Net display toggle is purely cosmetic.</p>
 */
export function calculateArbStakes(
  legs: { name: string; odds: number; netOdds?: number | null; bookie: string }[],
  totalStake: number
): ArbLeg[] {
  const totalImplied = legs.reduce(
    (sum, leg) => sum + americanToImpliedProbability(effectiveOdds(leg)),
    0
  );

  return legs.map((leg) => {
    const eff = effectiveOdds(leg);
    const prob = americanToImpliedProbability(eff);
    const stake = parseFloat(((prob / totalImplied) * totalStake).toFixed(2));
    const decimalOdds = americanToDecimal(eff);
    const payout = parseFloat((stake * decimalOdds).toFixed(2));
    return { ...leg, stake, payout };
  });
}

/**
 * Calculates the guaranteed profit from an arb.
 */
export function calculateArbProfit(legs: ArbLeg[], totalStake: number): number {
  const minPayout = Math.min(...legs.map((l) => l.payout));
  return parseFloat((minPayout - totalStake).toFixed(2));
}

/**
 * Formats American odds with +/- prefix.
 */
export function formatOdd(odd: number): string {
  if (odd === 0) return "--";
  return odd > 0 ? `+${odd}` : `${odd}`;
}

/**
 * Converts a UTC date string to user-friendly format.
 */
export function convertUTCtoUserTimezone(
  utcDateString: string,
  timeZone: string = "America/New_York"
): string {
  const date = new Date(utcDateString);
  const options: Intl.DateTimeFormatOptions = {
    timeZone,
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  return new Intl.DateTimeFormat("en-US", options).format(date);
}

/**
 * Formats a dollar amount.
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

/**
 * Converts a market key like "player_double_double" to "Player Double Double".
 */
export function formatMarketKey(key: string): string {
  if (!key) return "";
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Groups legs by outcome name. For each unique outcome, returns the best odds
 * and ALL bookies that share that same best price (up to 2).
 * Mirrors +EV's filterBetsWithDifferentPrice pattern.
 */
export interface GroupedLeg {
  name: string;
  /** Listed (pre-fee) American odds — the headline display number. */
  odds: number;
  /** Net (post-fee) American odds. Equal to {@link #odds} for sportsbook
   *  rows; for prediction-market rows, what the user actually nets. */
  netOdds?: number | null;
  bookies: string[];   // all bookies at the same best price
  /** Per-bookie {@link ArbLeg}s tied at the chosen best price.
   *
   *  Where {@link bookies} carries display strings only, this carries
   *  the full leg shape — including {@code outcomeLink} — so the
   *  no-extension "Add to slip" CTA can pick ANY tied book whose
   *  bookie key is deeplink-supported (not just the first listed).
   *
   *  Aligned with {@link bookies} when the backend emits per-bookie
   *  legs (the standard `buildLegsForSide` path). May be a strict
   *  subset when an upstream filter pre-reduced the row and only
   *  populated {@link ArbLeg.bookies} as a string list — in that
   *  legacy case the deeplink CTA may still resolve, but only for
   *  the bookie whose leg shape made it through. */
  bestLegs: ArbLeg[];
  /** True when the chosen best leg is on a prediction market. Lets the
   *  cell decide whether to mount the listed/net fee toggle. */
  isPredictionMarket?: boolean;
  stake: number;
  payout: number;
  /** Forwarded from the underlying ArbLeg whose bookie is "kalshi", if any
   *  leg in the group has a kalshi ticker. Lets the cell render the inline
   *  "Place on Kalshi" CTA without a second pass over the raw legs.
   *
   *  CRITICAL: this is per-group (per-side), not per-row. An arb pairs two
   *  outcomes that may live on different thresholds (e.g. Over 27.5 vs
   *  Under 30.5); reusing one row-wide ticker for both sides routes the
   *  OVER click to the wrong threshold. Each group's ticker only comes
   *  from the kalshi leg WITHIN that group. */
  kalshiTicker?: string;
  /** Threshold parsed off the kalshi ticker (e.g. 29.5 from
   *  {@code …-T29.5}). Lets the cell guard against the rare server-side
   *  case where The Odds API mis-attaches a sid to an outcome whose
   *  point value doesn't match. Null for non-totals tickers. */
  kalshiTickerThreshold?: number | null;
  /**
   * Best-priced leg in the group whose bookie is deeplink-supported AND
   * carries an {@code outcomeLink} — but that is NOT among the
   * tied-at-best legs in {@link bestLegs}.
   *
   * Why this exists: when the chart's headline price is at a DFS app
   * (PrizePicks / betr / pick6 — boosted lines, no deeplink) or on a
   * prediction market (Kalshi), the user has no actionable "Add to
   * slip" CTA even though a real sportsbook offers the same outcome
   * at a worse price. Without a fallback, ~50% of arb groups silently
   * lose their deeplink CTA.
   *
   * The cell renders the fallback CTA with the bookie name + price
   * baked into the label so the click target is explicit (e.g.
   * {@code "Add to DraftKings -670"}) — no bait-and-switch with
   * the chart's displayed best price.
   *
   * Null when (a) the tied-at-best set already has a usable deeplink
   * leg (no fallback needed) or (b) no leg in the group is both
   * supported and link-populated.
   */
  fallbackDeeplinkLeg?: ArbLeg | null;
}

export function selectTopTwoBets(legs: ArbLeg[]): GroupedLeg[] {
  // Group legs by name
  const groups: Record<string, ArbLeg[]> = {};
  for (const leg of legs) {
    if (!groups[leg.name]) {
      groups[leg.name] = [];
    }
    groups[leg.name].push(leg);
  }

  const result: GroupedLeg[] = [];
  const names = Object.keys(groups);
  for (let i = 0; i < names.length; i++) {
    const name = names[i];
    const groupLegs: ArbLeg[] = groups[name];
    const implied = (lg: ArbLeg) => americanToImpliedProbability(arbLegEffectiveAmerican(lg));
    // Pick the leg with the best (lowest-implied-prob) price using net odds
    // when present — matches backend selectBestArbBet + chart stake math.
    const best = groupLegs.reduce((a, b) => (implied(a) <= implied(b) ? a : b));
    const bestImp = implied(best);
    // Tied at effective price (epsilon for floating American conversions).
    const bestLegs = groupLegs.filter((l) => Math.abs(implied(l) - bestImp) < 1e-9);
    // Prefer the explicit tied-bookies list the filter may have attached; fall
    // back to scanning the group for legs matching the best price.
    const bookies =
      best.bookies && best.bookies.length > 0
        ? best.bookies.slice(0, 2)
        : bestLegs.map((l) => l.bookie).slice(0, 2);
    // Carry the ticker from any kalshi leg in THIS group, even if the chosen
    // best leg is from a sportsbook — the inline CTA should appear whenever
    // the user *can* take this side on Kalshi. We scope to {@code groupLegs}
    // (this side, this threshold) so an arb pairing different thresholds
    // doesn't leak the cross-side ticker over here. The cell additionally
    // guards on {@code kalshiTickerThreshold} vs the leg label.
    const kalshiLeg = groupLegs.find(
      (l) => l.bookie === "kalshi" && !!l.kalshiTicker
    );
    const kalshiTickerThreshold = kalshiLeg?.kalshiTickerThreshold ??
      parseKalshiTickerThreshold(kalshiLeg?.kalshiTicker);
    const isPredictionMarket = best.bookie
      ? PREDICTION_MARKET_BOOKIES.has(best.bookie)
      : false;
    // Net odds: prefer the chosen leg's netOdds (post-fee) when present;
    // for older API responses without the field we fall back to the
    // listed odds so downstream code can treat netOdds as always-defined.
    const netOdds = best.netOdds ?? best.odds;
    // Fallback deeplink leg: best-priced leg with deeplink+outcomeLink
    // that lives OUTSIDE the tied-at-best set. Powers the
    // "Add to <bookie> <price>" CTA for groups whose headline price is
    // at a DFS app or PM and would otherwise show no actionable button.
    //
    // Two-tier resolution:
    //   1. Honor a {@link ArbLeg.sideFallbackDeeplinkLeg} that the arb
    //      page's `bestifyLegsForDisplay` pre-computed from the FULL
    //      per-bookie group BEFORE collapsing to one leg per side. Once
    //      bestify has run, this is the only path that can find a
    //      worse-priced fallback because step (2) below sees only the
    //      one chosen leg per side and the walk is dead code.
    //   2. Otherwise (non-bestified shapes — share-mode injection, +EV
    //      reuse, raw responses) fall back to walking {@code groupLegs}
    //      ourselves; this is the original behaviour and remains
    //      correct for callers that pass us full per-bookie legs.
    //
    // Sorted by effective implied prob ascending so the first match is
    // the cheapest deeplinkable price the user can take.
    const tiedKeys = new Set(
      bestLegs.map((l) => `${l.bookie ?? ""}|${l.odds}|${l.netOdds ?? l.odds}`)
    );
    const tiedHasUsableDeeplink = bestLegs.some(
      (l) => l.bookie && bookSupportsDeeplink(l.bookie) && !!l.outcomeLink
    );
    let fallbackDeeplinkLeg: ArbLeg | null = null;
    if (!tiedHasUsableDeeplink) {
      // Prefer the pre-computed value if any leg in the tied set carries
      // it (the bestify path attaches it onto the chosen leg). Falling
      // through to the in-place walk lets non-bestified callers still
      // resolve a fallback the legacy way.
      const preComputed = bestLegs
        .map((l) => l.sideFallbackDeeplinkLeg)
        .find((v) => v != null);
      fallbackDeeplinkLeg =
        preComputed ??
        groupLegs
          .slice()
          .sort((a, b) => implied(a) - implied(b))
          .find(
            (l) =>
              !!l.bookie &&
              bookSupportsDeeplink(l.bookie) &&
              !!l.outcomeLink &&
              !tiedKeys.has(
                `${l.bookie ?? ""}|${l.odds}|${l.netOdds ?? l.odds}`
              )
          ) ??
        null;
    }
    result.push({
      name,
      odds: best.odds,
      netOdds,
      isPredictionMarket,
      bookies,
      bestLegs,
      stake: best.stake,
      payout: best.payout,
      kalshiTicker: kalshiLeg?.kalshiTicker,
      kalshiTickerThreshold,
      fallbackDeeplinkLeg,
    });
  }

  return result;
}

/**
 * When true, log a one-line breakdown for any side group whose chosen
 * pair has no usable "Add to slip" CTA after the fallback search. Helps
 * diagnose the historical "Add to slip is hidden even for supported
 * books" bug by surfacing whether the upstream legs lacked
 * {@code outcomeLink}, the bookie key didn't canonicalize, or the
 * user's book filter excluded every deeplinkable leg. Cheap (one log
 * per affected row, ~50 rows max) so it's safe to leave on in dev;
 * remove once production telemetry confirms the fix has landed.
 */
const ARB_DEEPLINK_DEBUG =
  typeof process !== "undefined" && process.env?.NODE_ENV !== "production";

function logMissingDeeplinkDiagnostic(
  rowId: string,
  side: string,
  legs: ArbLeg[]
): void {
  if (!ARB_DEEPLINK_DEBUG) return;
  // eslint-disable-next-line no-console
  console.warn(
    `[arb deeplink] no usable CTA for row=${rowId} side="${side}" — ${legs.length} candidate leg(s):`,
    legs.map((l) => ({
      bookie: l.bookie,
      odds: l.odds,
      netOdds: l.netOdds ?? null,
      hasOutcomeLink: !!l.outcomeLink,
      supportsDeeplink: !!l.bookie && bookSupportsDeeplink(l.bookie),
    }))
  );
}

/**
 * Reduces a row's full list of per-book legs to one best-priced leg per
 * outcome, across ALL outcomes in the market (2 for a two-way market,
 * 3 for soccer three-way moneyline, etc.).
 *
 * Backend emits one row per market, with every supported bookmaker on
 * every outcome as its own leg. When the user picks a book filter, we keep
 * only the allowed books, then pick the best-priced leg per outcome name.
 * The chosen legs carry a `bookies` array with up to 2 books tied at the
 * top price (so the cell can show both icons when applicable).
 *
 * <p>Critically, before collapsing each per-bookie group down to the
 * single chosen leg, we compute a per-side fallback "Add to slip"
 * target — the best-priced deeplink-supported leg with
 * {@code outcomeLink} populated that lives OUTSIDE the tied-at-best
 * set. The cell can then surface an "Add to {Book} {price}" CTA when
 * the headline best leg is at PrizePicks / Kalshi / a non-deeplinkable
 * book. Without this, the equivalent walk in
 * {@code selectTopTwoBets} is dead code on the arb chart because the
 * per-side group it sees is exactly one leg by the time the cell calls
 * it.</p>
 *
 * <p>Respects the user's book filter: the fallback walks
 * {@code srcLegs}, which is already book-filtered when
 * {@code allowedBookies != null}, so the CTA can never resolve to a
 * book the user explicitly excluded.</p>
 *
 * Returns null when:
 *   - any outcome of the market loses ALL of its books to the DFS or
 *     book filter. An arb must cover every outcome; recomputing arb%
 *     from a partial set produces a phantom "arb" whose displayed
 *     margin is wildly inflated (e.g. a 3-way 13.89% row showing
 *     +38.28% from just two of its legs) and whose follower is exposed
 *     to the uncovered outcome, or
 *   - the best-per-outcome set within the allowed books no longer arbs.
 */
export function bestifyLegsForDisplay(
  row: ArbitrageRow,
  allowedBookies: Set<string> | null,
  keepNonPositive: boolean = false
): ArbitrageRow | null {
  if (!row.legs || row.legs.length === 0) return null;

  // The full market's outcome count, BEFORE any filtering. Every one of
  // these must survive filtering for the row to remain an executable arb.
  const requiredOutcomes = new Set(row.legs.map((l) => l.name)).size;

  // DFS / pick'em apps (PrizePicks / Underdog / Pick6 / Betr Picks)
  // are never bettable as a single leg — the posted "odds" only pay
  // when bundled into a 6-pick parlay, so any arb anchored to a DFS
  // leg is a phantom. The server already strips DFS from the arb feed
  // (see {@link PlayerPropsService#applyArbFilterCriteria}); this
  // pass is defense-in-depth for shared payloads / older rows that
  // pre-date the server filter, and for the prediction-market arb
  // endpoint which builds its own legs list.
  const dfsFreeLegs = row.legs.filter(
    (l) => getSourceTypeForBookmaker(normalizeBookieKey(l.bookie)) !== "dfs"
  );
  if (dfsFreeLegs.length === 0) return null;

  const srcLegs = allowedBookies
    ? dfsFreeLegs.filter((l) => allowedBookies.has(normalizeBookieKey(l.bookie)))
    : dfsFreeLegs;
  if (srcLegs.length === 0) return null;

  // Preserve the insertion order of outcome names so we don't flip sides.
  const byName = new Map<string, ArbLeg[]>();
  for (const leg of srcLegs) {
    const list = byName.get(leg.name);
    if (list) list.push(leg);
    else byName.set(leg.name, [leg]);
  }
  // Full-coverage gate: fewer surviving outcomes than the market has means
  // the "arb" can't be placed — hide the row rather than mislead.
  if (byName.size < 2 || byName.size < requiredOutcomes) return null;

  const names = Array.from(byName.keys());
  const implied = (lg: ArbLeg) => americanToImpliedProbability(arbLegEffectiveAmerican(lg));
  const bestLegs = names.map((name) =>
    byName.get(name)!.reduce((a, b) => (implied(a) <= implied(b) ? a : b))
  );
  // Match the server's cross-book requirement. Independent best-per-side
  // picks can collapse a valid cross-book arb back into a same-book pair.
  if (new Set(bestLegs.map((l) => normalizeBookieKey(l.bookie))).size === 1) {
    const bestBook = normalizeBookieKey(bestLegs[0].bookie);
    let replacement: { index: number; leg: ArbLeg; cost: number } | null = null;
    for (const [index, name] of names.entries()) {
      for (const leg of byName.get(name)!) {
        if (normalizeBookieKey(leg.bookie) === bestBook) continue;
        const cost = implied(leg) - implied(bestLegs[index]);
        if (!replacement || cost < replacement.cost) replacement = { index, leg, cost };
      }
    }
    if (!replacement) return null;
    const { index, leg } = replacement;
    bestLegs[index] = leg;
  }
  // Stable signature for "is this leg in the tied-at-best set" checks.
  // Same shape used by selectTopTwoBets so the two layers agree.
  const legKey = (l: ArbLeg): string =>
    `${l.bookie ?? ""}|${l.odds}|${l.netOdds ?? l.odds}`;

  const chosen: ArbLeg[] = names.map((name, index) => {
    const legs = byName.get(name)!.filter((candidate) =>
      bestLegs.some((other, otherIndex) => otherIndex !== index &&
        normalizeBookieKey(other.bookie) !== normalizeBookieKey(candidate.bookie))
    );
    const best = bestLegs[index];
    const bestImp = implied(best);
    const tiedLegs = legs.filter(
      (l) => Math.abs(implied(l) - bestImp) < 1e-9
    );
    const tiedBookies = [best, ...tiedLegs.filter((l) => l !== best)]
      .map((l) => l.bookie).slice(0, 2);

    // Walk the FULL per-bookie group (post book-filter) for a fallback
    // BEFORE we collapse to one leg. If the headline tied set already
    // carries a usable deeplink, no fallback is needed (null). The
    // walk is sorted by ascending implied probability so the first hit
    // is the best price the user can actually click through.
    const tiedHasUsableDeeplink = tiedLegs.some(
      (l) => !!l.bookie && bookSupportsDeeplink(l.bookie) && !!l.outcomeLink
    );
    let sideFallbackDeeplinkLeg: ArbLeg | null = null;
    if (!tiedHasUsableDeeplink) {
      const tiedKeys = new Set(tiedLegs.map(legKey));
      sideFallbackDeeplinkLeg =
        legs
          .slice()
          .sort((a, b) => implied(a) - implied(b))
          .find(
            (l) =>
              !!l.bookie &&
              bookSupportsDeeplink(l.bookie) &&
              !!l.outcomeLink &&
              !tiedKeys.has(legKey(l))
          ) ?? null;
      if (!sideFallbackDeeplinkLeg) {
        logMissingDeeplinkDiagnostic(row.id, name, legs);
      }
    }

    return { ...best, bookies: tiedBookies, sideFallbackDeeplinkLeg };
  });

  const arbPct = calculateArbPercent(chosen);
  // Shared links keep non-positive rows so the share page can render the
  // original opportunity with an "edge moved" / expired state instead of
  // vanishing into "No results". The live board never passes this flag.
  if (arbPct <= 0 && !keepNonPositive) return null;

  return { ...row, legs: chosen, arb_percent: arbPct };
}

/**
 * Drops rows whose `id` repeats, keeping the first occurrence. The
 * pre-match path merges two feeds (sports + prediction-markets) and a
 * repeated id would render the same opportunity twice on the board.
 */
export function dedupeArbRowsById(rows: ArbitrageRow[]): ArbitrageRow[] {
  const seen = new Set<string>();
  const out: ArbitrageRow[] = [];
  for (const row of rows) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
  }
  return out;
}

/**
 * Gets arb calculator values from an ArbitrageRow.
 */
export function getArbCalcValues(row: ArbitrageRow): ArbCalculatorValues {
  return {
    event: row.event,
    market: row.market,
    legs: row.legs,
    arb_percent: row.arb_percent,
  };
}

// ── Stake rounding ──────────────────────────────────────────────────────────

/**
 * Rounds a stake to the nearest whole dollar.
 * Avoids alarming books with precise amounts like $47.83.
 */
export function roundStakeNatural(stake: number): number {
  if (stake <= 0) return 0;
  return Math.round(stake);
}

/**
 * Wraps calculateArbStakes then rounds each leg's stake to natural amounts.
 * Recalculates payouts from the rounded stakes.
 */
export function calculateArbStakesRounded(
  legs: { name: string; odds: number; netOdds?: number | null; bookie: string }[],
  totalStake: number
): ArbLeg[] {
  const exact = calculateArbStakes(legs, totalStake);
  return exact.map((leg) => {
    const rounded = roundStakeNatural(leg.stake);
    const decimalOdds = americanToDecimal(effectiveOdds(leg));
    const payout = parseFloat((rounded * decimalOdds).toFixed(2));
    return { ...leg, stake: rounded, payout };
  });
}

/**
 * Calculates profit from rounded stakes - min payout minus sum of rounded stakes.
 */
export function calculateArbProfitRounded(legs: ArbLeg[]): number {
  const totalRoundedStake = legs.reduce((sum, l) => sum + l.stake, 0);
  const minPayout = Math.min(...legs.map((l) => l.payout));
  return parseFloat((minPayout - totalRoundedStake).toFixed(2));
}

/**
 * Returns per-leg profit outcomes from rounded stakes.
 * Each entry shows the profit if that particular leg wins.
 */
export function calculateProfitByLeg(legs: ArbLeg[]): { name: string; profit: number }[] {
  const totalRoundedStake = legs.reduce((sum, l) => sum + l.stake, 0);
  return legs.map((leg) => ({
    name: leg.name,
    profit: parseFloat((leg.payout - totalRoundedStake).toFixed(2)),
  }));
}
